import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { startAmbientOrb } from './ambientOrb';
import  SamparkAiAssistantWidget  from '@samparkchat/sampark-web-chat/sampark/sampark-chat/screens/SamparkAiAssistantWidget';
import './AIEmployee.css';

const assetBase = `${process.env.PUBLIC_URL}/ai-employee-assets`;

/** Opaque URL query keys (not named appId/secretKey/botId). */
const QUERY_APP = 'a';
const QUERY_KEY = 'k';
const QUERY_BOT = 'b';

const fontFaces = `
@font-face {
  font-family: "Sampark AI Employee";
  src: url("${assetBase}/assets/Poppins-Regular.ttf") format("truetype");
  font-weight: 400;
  font-style: normal;
}
@font-face {
  font-family: "Sampark AI Employee";
  src: url("${assetBase}/assets/Poppins-Medium.ttf") format("truetype");
  font-weight: 500;
  font-style: normal;
}
@font-face {
  font-family: "Sampark AI Employee";
  src: url("${assetBase}/assets/Poppins-SemiBold.ttf") format("truetype");
  font-weight: 600;
  font-style: normal;
}
@font-face {
  font-family: "Sampark AI Employee";
  src: url("${assetBase}/assets/Poppins-Bold.ttf") format("truetype");
  font-weight: 700;
  font-style: normal;
}
`;

const AIEmployee: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [searchParams] = useSearchParams();
  const [appId, setAppId] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [botId, setBotId] = useState('');

  useEffect(() => {
    const nextAppId = searchParams.get(QUERY_APP) || '';
    const nextSecretKey = searchParams.get(QUERY_KEY) || '';
    const nextBotId = searchParams.get(QUERY_BOT) || '';
    setAppId(nextAppId);
    setSecretKey(nextSecretKey);
    setBotId(nextBotId);
    console.log('AI Employee appId:', nextAppId);
    console.log('AI Employee secretKey:', nextSecretKey);
    console.log('AI Employee botId:', nextBotId);
  }, [searchParams]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'AI Employee · Sampark';

    const themeMeta = document.querySelector('meta[name="theme-color"]');
    const previousTheme = themeMeta?.getAttribute('content');
    themeMeta?.setAttribute('content', '#f8fbff');

    const canvas = canvasRef.current;
    const stopOrb = canvas ? startAmbientOrb(canvas) : undefined;

    return () => {
      document.title = previousTitle;
      if (themeMeta && previousTheme) {
        themeMeta.setAttribute('content', previousTheme);
      }
      stopOrb?.();
    };
  }, []);

  return (
    <div className="ai-employee-page">
      <style>{fontFaces}</style>
      <div
        className="employee-screen"
        style={{ backgroundImage: `url(${assetBase}/background.svg)` }}
      >
        <header className="brand-bar">
          <img
            src={`${process.env.PUBLIC_URL}/images/sampark-logo.png`}
            alt="Sampark — Unite, Communicate, Collaborate"
          />
          <span className="brand-descriptor">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="m12 3 2.8 6.2L21 12l-6.2 2.8L12 21l-2.8-6.2L3 12l6.2-2.8Z" />
            </svg>
            AI, built around people
          </span>
        </header>

        <main className="hero">
          <div className="orb-scene" aria-hidden="true">
            <canvas id="ambient-orb" ref={canvasRef} />
          </div>
          <div className="eyebrow">Meet your AI employee</div>
          <h1>
            Your business.
            <br />
            <span>More possibilities.</span>
          </h1>
          <p className="description">
            An AI employee that understands your business,
            <br className="desktop-break" /> helps your customers, and supports your team.
          </p>
          <ul className="capabilities" aria-label="Capabilities">
            <li>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M9 5a3 3 0 0 0-5 3 4 4 0 0 0 0 8 3 3 0 0 0 5 3V5Zm6 0a3 3 0 0 1 5 3 4 4 0 0 1 0 8 3 3 0 0 1-5 3V5ZM9 9H6m9 6h3M9 15H6m9-6h3" />
              </svg>
              Understands your context
            </li>
            <li>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M20 11.5a8.5 8.5 0 0 1-8.5 8.5H4l-2 2V11.5A8.5 8.5 0 0 1 20 11.5ZM6 11h10m-10 4h6" />
              </svg>
              Helps in the moment
            </li>
            <li>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="m13 2-8 12h7l-1 8 8-12h-7Z" />
              </svg>
              Moves work forward
            </li>
          </ul>
        </main>
        <SamparkAiAssistantWidget
          appId={appId}
          secretKey={secretKey}
          varientId={''}
          title="Svara"
          registeredVisitor={ null }
        />
        <footer className="page-foot">
          <span>Human + AI. Better together.</span>
          <span>Powered by Sampark</span>
        </footer>
      </div>
      <div className="widget-mount" id="ai-employee-widget" />
    </div>
  );
};

export default AIEmployee;
